import Axios from "axios";
import { getAppEnv } from "../../config/app-env";
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  clearToken,
} from "./token-utils";

import showToast from "@/core/utils/show-toast";

const Api = getAppEnv();
const baseURL = Api.backendApi.url;

const axios = Axios.create({
  baseURL: `${baseURL}/api`,
  timeout: 20000, // ⭐ 20s is ideal
});


// ================= REQUEST =================

axios.interceptors.request.use((config) => {
  const token = getToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});


// ================= REFRESH CONTROL =================

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    error ? p.reject(error) : p.resolve(token);
  });

  failedQueue = [];
};


// ================= RESPONSE =================

axios.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // ⭐ HANDLE TIMEOUT FIRST
    if (
      error.code === "ECONNABORTED" ||
      /timeout/i.test(error.message)
    ) {
      showToast(
        "Server is taking too long to respond. Please try again.",
        "error"
      );

      return Promise.reject(error);
    }

    // ⭐ HANDLE NETWORK DOWN
    if (!error.response) {
      showToast(
        "Network error — please check your internet connection.",
        "error"
      );

      return Promise.reject(error);
    }

    // ================= 401 =================

    if (error.response.status === 401 && !originalRequest._retry) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearToken();
        window.location.href = "/auth/login";
        return Promise.reject(error);
      }

      try {
        const res = await Axios.post(
          `${baseURL}/api/auth/refresh-token`,
          { refreshToken }
        );

        const data = res.data.data || res.data;
        const newAccessToken = data.accessToken || data.token;
        const newRefreshToken = data.refreshToken;

        setToken(newAccessToken);

        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
        }

        axios.defaults.headers.common.Authorization =
          `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return axios(originalRequest);

      } catch (refreshError) {

        processQueue(refreshError, null);

        clearToken();
        window.location.href = "/auth/login";

        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
